<template>
  <div>
    <PageDynamicBreadcrumb />

    <PageHeader title="Create New Agency Business" />

    <q-form @submit="createBusiness">
      <FormCard
          title="Business Details"
      >
        <div class="row q-col-gutter-y-sm q-col-gutter-x-md">
          <q-input
              filled
              class="col-12"
              v-model="formData.business_name"
              label="Business Name"
              lazy-rules
              :rules="[
              val =>
                (val && val.length > 1) ||
                'Enter a name for the business.'
              ]"
              :disable="submitting"
          />
          <q-select
              filled
              class="col-12"
              v-model="formData.agency_type"
              :options="[{
                label: 'Captive Agency',
                value: 'Captive'
              },
              {
                label: 'Independent Agency',
                value: 'Independent'
              }]"
              label="Agency Type"
              emit-value
              map-options
              lazy-rules
              :rules="[
              val =>
                (val && val.length > 0) ||
                'Select the business type.'
              ]"
              :disable="submitting"
          />
          <q-input
              filled
              class="col-12"
              v-model="formData.business_phone"
              label="Business Phone Number"
              mask="(###) ###-####"
              unmasked-value
              lazy-rules
              :rules="[
            val =>
              (val && val.length > 9) ||
              'Enter a phone number for the business.'
            ]"
              :disable="submitting"
              hint="By default, calls to this businesses' profiles will be sent to this number."
          />
          <q-input
              filled
              class="col-12"
              v-model="formData.business_website_url"
              label="Business Website Url (Optional)"
              :disable="submitting"
              hint="By including website url, everybody gets at least one form of 'secondary engagement' right off the bat."
          />
        </div>
      </FormCard>

      <FormCard
          title="Business Location"
          class="q-mt-lg"
      >
        <div class="row q-col-gutter-y-sm q-col-gutter-x-md">
          <q-input
              filled
              class="col-12"
              v-model="formData.address"
              label="Street Address"
              lazy-rules
              :rules="[
              val =>
                (val && val.length > 1) ||
                'Enter a street address.'
              ]"
              :disable="submitting"
          />
          <q-input
            filled
            class="col-12"
            v-model="formData.address_line_2"
            label="Apt/Suite/Etc #"
            :disable="submitting"
            bottom-slots
          />

          <q-input
              filled
              class="col-6"
              v-model="formData.city"
              label="City"
              lazy-rules
              :rules="[
              val =>
                (val && val.length > 1) ||
                'Enter a city.'
              ]"
              :disable="submitting"
          />

          <q-select
              filled
              class="col-3"
              v-model="formData.state"
              label="State"
              :options="generateStatesSelectOptionsArray()"
              map-options
              emit-value
              lazy-rules
              :rules="[
              val =>
                (val && val.length > 1) ||
                'Select a state.'
              ]"
              :disable="submitting"
          />

          <q-input
              filled
              class="col-3"
              v-model="formData.postal_code"
              label="Postal Code"
              mask="#####"
              lazy-rules
              :rules="[
              val =>
                (val && val.length > 4) ||
                'Enter a postal code.'
              ]"
              :disable="submitting"
          />
        </div>
      </FormCard>

      <FormCard title="Google IDs" message="TODO: Allow for using these to pull reviews (directly from google or via scrape) to populate Reviews." class="q-mt-lg">
        <div class="row q-col-gutter-y-sm q-col-gutter-x-md">
          <q-input
            filled
            class="col-6"
            v-model="formData.google_cid"
            label="Google CID"
            :disable="submitting"
          />
          <q-input
            filled
            class="col-6"
            v-model="formData.google_place_id"
            label="Google Place ID"
            :disable="submitting"
          />
        </div>
      </FormCard>

      <FormActionsSection>
        <template v-slot:primary-actions>
          <q-btn
              label="Create Business"
              type="submit"
              color="primary"
              :disable="submitting"
              :loading="submitting"
          />
        </template>
      </FormActionsSection>
    </q-form>

    <q-dialog v-model="showConfirmAddressModal">
      <q-card>
        <q-card-section class="flex items-center justify-start no-wrap">
          <div>
            <h5>Confirm Your Address</h5>
            <p class="text-font-secondary">
              Couldn't confirm the address. Select the correct address below or review the address entered.
            </p>
          </div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <p class="text-weight-medium q-mb-xs">Address Entered:</p>
          <p>{{ formData.address }}</p>
          <p v-if="formData.address_line_2.length > 0">{{ formData.address_line_2 }}</p>
          <p>{{ formData.city + ", " + getFullStateName(formData.state) + " " + formData.postal_code + ", United States"}}</p>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <p class="text-weight-medium q-mb-sm">Suggested Addresses:</p>
          <div class="q-gutter-y-sm">
            <q-btn
                v-for="(addressOption, i) in confirmAddressOptions"
                :key="i + '_confirmAddressOption'"
                outline
                no-caps
                :color="confirmedAddress !== null && confirmedAddress.id == addressOption.id ? 'primary' : 'subtle'"
                align="left"
                class="full-width"
                :class="{ 'bg-primary-light': confirmedAddress !== null && confirmedAddress.id == addressOption.id }"
                @click="() => { confirmedAddress = addressOption; }"
                style="line-height: 1.35; padding: 12px"
            >
              <div class="flex items-center justify-start">
                <q-icon
                    name="location_on"
                    :color="confirmedAddress !== null && confirmedAddress.id == addressOption.id ? 'primary' : 'font-secondary'"
                    size="30px"
                    class="q-mr-md text-weight-regular"
                />
              </div>
              <div class="text-left">
                <p class="text-font q-mb-none">{{ addressOption.properties.name }}</p>
                <p v-if="formData.address_line_2.length > 0" class="text-font q-mb-none">{{ formData.address_line_2 }}</p>
                <p class="text-font">{{ addressOption.properties.place_formatted }}</p>
              </div>
            </q-btn>
          </div>
        </q-card-section>

        <q-card-actions align="between" class="q-gutter-x-md q-pa-md">
          <q-btn
            flat
            @click="() => { showConfirmAddressModal = false; }"
            label="Cancel"
            color="subtle"
            v-close-popup
          />

          <q-btn
            @click="() => { confirmAddress(confirmedAddress); }"
            label="Confirm & Save"
            color="primary"
            :disable="!confirmedAddress"
          >
            <q-tooltip v-if="!confirmedAddress" anchor="top middle" self="bottom middle">
              Select an address to continue.
            </q-tooltip>
          </q-btn>
        </q-card-actions>

      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import FormActionsSection from "@/components/UI/FormActionsSection.vue";
import FormCard from "@/components/UI/FormCard.vue";
import axios from "axios";
import PageHeader from "@/components/UI/PageHeader.vue";
import PageDynamicBreadcrumb from "@/components/UI/PageDynamicBreadcrumb.vue";

export default {
  name: "InsuranceAgentsBusinessCreator",
  components: {PageDynamicBreadcrumb, PageHeader, FormCard, FormActionsSection},
  props: {
    pendingSignupId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      submitting: false,
      formData: {
        business_name: "",
        business_type: "",
        agency_type: "",
        business_phone: "",
        business_website_url: "",
        address: "",
        address_line_2: "",
        city: "",
        state: "",
        postal_code: "",
        google_cid: "",
        google_place_id: "",
      },
      showConfirmAddressModal: false,
      confirmAddressOptions: [],
      confirmedAddress: null
    };
  },
  mounted() {
    axios("http://localhost:8082/professionals/pending-business-signups", {
      params: {
        id: this.pendingSignupId,
      }
    })
      .then(resp => {
        let pendingSignupResponse = resp.data.data;
        if (pendingSignupResponse.length > 0) {
          let formData = pendingSignupResponse[0];
          formData.google_cid = "";
          formData.google_place_id = "";
          formData.address_line_2 = "";
          this.formData = formData;
        }
      });
  },
  methods: {
    createBusiness() {
      this.submitting = true;

      this.validateLocation()
        .then(() => {
          axios
            .post("http://localhost:8082/internal/insurance-agents/business/create", {
              pending_signup_id: this.pendingSignupId,
              business_name: this.formData.business_name,
              business_type_id: 1,
              business_subtype_id: this.formData.agency_type === 'Captive' ? 1 : 2,
              business_phone: this.formData.business_phone,
              business_website_url: this.formData.business_website_url,
              address: this.formData.address + (this.formData.address_line_2.length > 0 ? ' ' + this.formData.address_line_2.length > 0 : ''),
              city: this.formData.city,
              state: this.formData.state,
              postal_code: this.formData.postal_code,
              latitude: this.formData.latitude,
              longitude: this.formData.longitude
            })
            .then(() => {
              this.triggerActionOutcomeAlert("create", true);
              this.$router.push("/insurance-agents/pending-signups");
            })
            .catch(err => {
              console.log(err);
              this.triggerActionOutcomeAlert("create", false);
            })
            .finally(() => {
              this.submitting = false;
            });
        })
        .catch(() => {
          this.submitting = false;
        });
    },
    validateLocation() {
      return new Promise(( resolve, reject ) => {
        axios.get("https://api.mapbox.com/search/geocode/v6/forward", {
          params: {
            address_line1: this.formData.address,
            place: this.formData.city,
            region: this.formData.state,
            postcode: this.formData.postal_code,
            country: "US",
            types: "address",
            access_token: "pk.eyJ1IjoianVtcGZlZWRhcmVuIiwiYSI6ImNtZTU1OXJ0cDBxNTEybnB3emtlYnlhdmcifQ.wT9BYPEwcGHqp5vVb2ltKg"
          }
        })
            .then(resp => {
              let addressMatches = resp.data.features;
              let addressSuggestions = addressMatches.filter(addressMatch => addressMatch.properties.feature_type === "address" && ('match_code' in addressMatch.properties && 'confidence' in addressMatch.properties.match_code && (addressMatch.properties.match_code.confidence !== "low")));

              if (addressSuggestions.length === 0) {
                this.triggerActionOutcomeAlert('created', false, "Your address is invalid. Please review and try again.");
                reject();
              }
              else if (addressSuggestions.length === 1 && (this.formData.city == addressSuggestions[0].properties.context.place.name && this.formData.state == addressSuggestions[0].properties.context.region.region_code && this.formData.postal_code == addressSuggestions[0].properties.context.postcode.name)) {
                this.formData.latitude = addressSuggestions[0].properties.coordinates.latitude;
                this.formData.longitude = addressSuggestions[0].properties.coordinates.longitude;
                resolve();
              }
              else {
                let matchFound = false;
                addressSuggestions.forEach(addressSuggestion => {
                  if (
                      (addressSuggestion.properties.match_code.confidence === "exact")
                      ||
                      (this.formData.address == addressSuggestion.properties.context.address.name &&
                          this.formData.city == addressSuggestion.properties.context.place.name &&
                          this.formData.state == addressSuggestion.properties.context.region.region_code &&
                          this.formData.postal_code == addressSuggestion.properties.context.postcode.name)
                  ) {
                    this.formData.latitude = addressSuggestions[0].properties.coordinates.latitude;
                    this.formData.longitude = addressSuggestions[0].properties.coordinates.longitude;
                    matchFound = true;
                  }
                });

                if (!matchFound) {
                  this.confirmAddressOptions = addressSuggestions;
                  this.showConfirmAddressModal = true;
                  reject();
                } else {
                  resolve();
                }
              }
            });
      });
    },
    confirmAddress(confirmedAddress) {
      this.formData.address = confirmedAddress.properties.context.address.name;
      this.formData.city = confirmedAddress.properties.context.place.name;
      this.formData.state = confirmedAddress.properties.context.region.region_code;
      this.formData.postal_code = confirmedAddress.properties.context.postcode.name;

      this.createBusiness();
    }
  }
};
</script>

<style scoped lang="scss"></style>